/**
 * Component: ReportingPage
 * SpecReferenceID: REPORTING-V1
 * Requirements: REPORTING-V1-R12
 *
 * Administrator reporting page. Surfaces the REPORTING-V1 backend
 * endpoints (R01 to R11) so admins can trigger classification runs,
 * compute daily KPIs, and download CSV exports without using curl or
 * Swagger. See the Phase 6 section of reporting-implementation-plan.md
 * for full design notes.
 *
 * Page layout (top to bottom):
 *   1. DatePresetPicker with UTC-day semantics
 *   2. Actions row: Generate & Download (chained) + 3 granular actions
 *   3. Status panels: classification coverage, daily KPIs table, recent
 *      chat turns preview table
 *
 * Role gate: the /admin/reporting route is wrapped in ProtectedRoute
 * with requiredRoles=['admin'] and FeatureRoute featureKey='admin'.
 * This component therefore trusts that an admin user is rendering it;
 * the endpoints themselves re-enforce admin via AdminUserDep
 * (REPORTING-V1-R10).
 *
 * Accessibility: WCAG 2.1 AA. All actions are keyboard accessible,
 * progress states have aria-live announcements, and disabled states
 * set aria-disabled.
 *
 * Risk Links: R6
 * FMEA Links: FM5
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AlertCircle,
  Download,
  FileText,
  Loader2,
  PlayCircle,
  Sparkles,
} from 'lucide-react';

import { getErrorMessage } from '@/lib/api';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

import { DatePresetPicker, initialRange } from './components/DatePresetPicker';
import type { ResolvedRange } from './components/DatePresetPicker';
import {
  computeDailyKpis,
  downloadDailyReportCsv,
  getClassificationStatus,
  listChatTurns,
  listDailyKpis,
  runBatchClassify,
  triggerBrowserDownload,
  waitForClassificationRun,
  type ChatTurnItem,
  type ClassificationRun,
  type ClassificationStatus,
  type DailyKpiItem,
} from './reporting-api';

/**
 * Per-step progress signal emitted by the chained "generate & download"
 * action. Kept as a flat string union so the UI can render a single
 * aria-live region without juggling nested state.
 */
type ChainStep =
  | 'idle'
  | 'classifying'
  | 'computing_kpis'
  | 'downloading'
  | 'done'
  | 'error';

function reportFilename(range: ResolvedRange): string {
  const startTag = range.start.slice(0, 10);
  const endTag = new Date(new Date(range.end).getTime() - 1)
    .toISOString()
    .slice(0, 10);
  return `galen-daily-report-${startTag}_to_${endTag}.csv`;
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toFixed(2);
}

function truncate(str: string | null, max = 80): string {
  if (!str) return '—';
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

export function ReportingPage(): React.ReactElement {
  const { toast } = useToast();

  const [range, setRange] = useState<ResolvedRange>(() => initialRange());

  // Action state
  const [chainStep, setChainStep] = useState<ChainStep>('idle');
  const [chainMessage, setChainMessage] = useState<string>('');
  const [classifyInFlight, setClassifyInFlight] = useState(false);
  const [computeInFlight, setComputeInFlight] = useState(false);
  const [downloadInFlight, setDownloadInFlight] = useState(false);

  // Status panels
  const [status, setStatus] = useState<ClassificationStatus | null>(null);
  const [kpis, setKpis] = useState<DailyKpiItem[]>([]);
  const [chatTurns, setChatTurns] = useState<ChatTurnItem[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const anyActionInFlight =
    chainStep !== 'idle' &&
    chainStep !== 'done' &&
    chainStep !== 'error';
  const buttonsDisabled =
    anyActionInFlight ||
    classifyInFlight ||
    computeInFlight ||
    downloadInFlight;

  const refreshStatusPanels = useCallback(async (): Promise<void> => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const [statusRes, kpisRes, chatTurnsRes] = await Promise.all([
        getClassificationStatus(range),
        listDailyKpis(range),
        listChatTurns({ ...range, limit: 20, offset: 0 }),
      ]);
      setStatus(statusRes);
      setKpis(kpisRes.items);
      setChatTurns(chatTurnsRes.items);
    } catch (err) {
      setStatusError(getErrorMessage(err));
    } finally {
      setStatusLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void refreshStatusPanels();
  }, [refreshStatusPanels]);

  // REPORTING-V1-R06 (Bug #5070): the classify endpoint now returns 202
  // and drains in the background, so schedule the run then poll it to a
  // terminal outcome. Throws on a failed run so the chained "generate &
  // download" flow can abort; the standalone handler catches it.
  const runClassificationToCompletion =
    useCallback(async (): Promise<ClassificationRun> => {
      const scheduled = await runBatchClassify(range);
      const run = await waitForClassificationRun(scheduled.classificationRunId);
      if (run.outcome === 'failure') {
        throw new Error(
          run.errorMessage ?? 'Background classification run failed.',
        );
      }
      return run;
    }, [range]);

  const handleClassifyOnly = useCallback(async (): Promise<void> => {
    setClassifyInFlight(true);
    try {
      const run = await runClassificationToCompletion();
      const failedNote =
        run.failedCount > 0 ? `, ${run.failedCount} failed` : '';
      toast({
        title:
          run.outcome === 'partial_failure'
            ? 'Classification completed with errors'
            : 'Classification complete',
        description: `${run.classifiedCount} classified${failedNote} (of ${run.totalScannedCount} scanned)`,
      });
      await refreshStatusPanels();
    } catch (err) {
      toast({
        title: 'Classification failed',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setClassifyInFlight(false);
    }
  }, [runClassificationToCompletion, refreshStatusPanels, toast]);

  const handleComputeKpisOnly = useCallback(async (): Promise<void> => {
    setComputeInFlight(true);
    try {
      const res = await computeDailyKpis(range);
      toast({
        title: 'KPIs computed',
        description: `${res.daysUpserted} day(s) upserted`,
      });
      await refreshStatusPanels();
    } catch (err) {
      toast({
        title: 'KPI compute failed',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setComputeInFlight(false);
    }
  }, [range, refreshStatusPanels, toast]);

  const handleDownloadOnly = useCallback(async (): Promise<void> => {
    setDownloadInFlight(true);
    try {
      const blob = await downloadDailyReportCsv(range);
      triggerBrowserDownload(blob, reportFilename(range));
      toast({ title: 'Download started' });
    } catch (err) {
      toast({
        title: 'Download failed',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setDownloadInFlight(false);
    }
  }, [range, toast]);

  const handleGenerateAndDownload = useCallback(async (): Promise<void> => {
    // Step 1: classify (schedule + poll the background run to completion)
    setChainStep('classifying');
    setChainMessage('Step 1/3: Classifying turns (running in background)...');
    try {
      const run = await runClassificationToCompletion();
      const failedNote =
        run.failedCount > 0 ? ` (${run.failedCount} failed)` : '';
      setChainMessage(
        `Step 1/3: Classified ${run.classifiedCount}${failedNote}. Computing KPIs...`,
      );
    } catch (err) {
      setChainStep('error');
      setChainMessage(`Classification failed: ${getErrorMessage(err)}`);
      toast({
        title: 'Generate & download aborted',
        description: 'Classification step failed',
        variant: 'destructive',
      });
      return;
    }

    // Step 2: compute KPIs
    setChainStep('computing_kpis');
    try {
      const kpi = await computeDailyKpis(range);
      setChainMessage(
        `Step 2/3: ${kpi.daysUpserted} day(s) aggregated. Downloading...`,
      );
    } catch (err) {
      setChainStep('error');
      setChainMessage(`KPI compute failed: ${getErrorMessage(err)}`);
      toast({
        title: 'Generate & download aborted',
        description: 'KPI step failed',
        variant: 'destructive',
      });
      return;
    }

    // Step 3: download
    setChainStep('downloading');
    try {
      const blob = await downloadDailyReportCsv(range);
      triggerBrowserDownload(blob, reportFilename(range));
      setChainStep('done');
      setChainMessage('Report downloaded');
      toast({ title: 'Generate & download complete' });
    } catch (err) {
      setChainStep('error');
      setChainMessage(`Download failed: ${getErrorMessage(err)}`);
      toast({
        title: 'Download failed',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
      return;
    }

    await refreshStatusPanels();
    // Auto-clear the chain banner after a moment so the UI returns to neutral.
    setTimeout(() => {
      setChainStep((s) => (s === 'done' ? 'idle' : s));
    }, 4000);
  }, [range, runClassificationToCompletion, refreshStatusPanels, toast]);

  const coveragePercent = useMemo(() => {
    if (!status || status.totalCount === 0) return 0;
    return (status.classifiedCount / status.totalCount) * 100;
  }, [status]);

  return (
    <div className="container mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Reporting</h1>
        <p className="text-muted-foreground">
          Trigger LLM classification, compute daily KPIs, and download
          the consolidated daily report for a selected UTC date range.
        </p>
      </header>

      {/* Date range + actions */}
      <Card>
        <CardHeader>
          <CardTitle>Report range</CardTitle>
          <CardDescription>
            All ranges are interpreted as whole UTC calendar days. Presets
            end at the start of today UTC so reports cover only complete
            data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <DatePresetPicker
            value={range}
            onChange={setRange}
            disabled={buttonsDisabled}
          />

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Button
              onClick={() => void handleGenerateAndDownload()}
              disabled={buttonsDisabled}
              className="w-full"
              size="lg"
            >
              {anyActionInFlight ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" aria-hidden />
              )}
              Generate &amp; download
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleClassifyOnly()}
              disabled={buttonsDisabled}
            >
              {classifyInFlight ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <PlayCircle className="mr-2 h-4 w-4" aria-hidden />
              )}
              Classify only
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleComputeKpisOnly()}
              disabled={buttonsDisabled}
            >
              {computeInFlight ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <FileText className="mr-2 h-4 w-4" aria-hidden />
              )}
              Compute KPIs only
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleDownloadOnly()}
              disabled={buttonsDisabled}
            >
              {downloadInFlight ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="mr-2 h-4 w-4" aria-hidden />
              )}
              Download CSV only
            </Button>
          </div>

          {chainStep !== 'idle' && (
            <div
              role="status"
              aria-live="polite"
              className={
                chainStep === 'error'
                  ? 'rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive'
                  : 'rounded border border-border bg-muted/50 p-3 text-sm'
              }
            >
              {chainStep === 'error' && (
                <AlertCircle
                  className="mr-2 inline h-4 w-4"
                  aria-hidden
                />
              )}
              {chainMessage}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status panels */}
      {statusError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{statusError}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Classification coverage</CardTitle>
            <CardDescription>
              Turns in the selected range that already have an LLM
              classification stored.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {statusLoading && !status ? (
              <div className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Loading...
              </div>
            ) : status ? (
              <>
                <Progress value={coveragePercent} aria-label="Classification coverage" />
                <div className="flex justify-between text-sm">
                  <span>
                    {status.classifiedCount} / {status.totalCount} classified
                  </span>
                  <span className="text-muted-foreground">
                    {status.unclassifiedCount} unclassified
                  </span>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                No coverage data.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Daily KPIs</CardTitle>
            <CardDescription>
              Aggregated quality + engagement metrics per UTC day.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {kpis.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No KPI rows yet for this range. Run "Compute KPIs only" or
                the chained action to populate.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Turns</TableHead>
                      <TableHead className="text-right">Avg score</TableHead>
                      <TableHead className="text-right">Abandon</TableHead>
                      <TableHead className="text-right">Citations</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kpis.map((k) => (
                      <TableRow key={k.kpiDate}>
                        <TableCell className="font-mono text-xs">
                          {k.kpiDate}
                        </TableCell>
                        <TableCell className="text-right">{k.turns}</TableCell>
                        <TableCell className="text-right">
                          {formatNumber(k.avgFinalTurnScore)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatPercent(k.abandonRate)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatPercent(k.citationsRate)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent chat turns (preview)</CardTitle>
          <CardDescription>
            First 20 turns in the selected range. Full export via "Download
            CSV only" above or the consolidated daily report.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chatTurns.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No chat turns in this range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date (UTC)</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead className="text-right">Resp time (s)</TableHead>
                    <TableHead>Feedback</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chatTurns.map((t) => (
                    <TableRow key={t.questionId}>
                      <TableCell className="whitespace-nowrap font-mono text-xs">
                        {t.questionDate.slice(0, 19).replace('T', ' ')}
                      </TableCell>
                      <TableCell>{t.name ?? '—'}</TableCell>
                      <TableCell className="max-w-xs">
                        {truncate(t.question)}
                      </TableCell>
                      <TableCell className="text-right">
                        {t.responseTimeSeconds?.toFixed(2) ?? '—'}
                      </TableCell>
                      <TableCell>{truncate(t.feedback, 40)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ReportingPage;
